import { useEffect, useState, useCallback } from "react";
import Topbar from "@/components/Topbar";
import { apiClient, POLE_LABELS, formatDate, API } from "@/lib/api";
import { usePoles } from "@/context/PolesContext";
import StatusBadge from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, DownloadSimple } from "@phosphor-icons/react";

export default function Recherche() {
  const { poles, poleLabel } = usePoles();
  const [transactionNumber, setTransactionNumber] = useState("");
  const [q, setQ] = useState("");
  const [poleFilter, setPoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (transactionNumber) params.transaction_number = transactionNumber;
      if (q) params.q = q;
      if (poleFilter !== "all") {
        params.pole = poleFilter;
        params.direction = "all";
      }
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await apiClient.get("/requests", { params });
      setItems(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [transactionNumber, q, poleFilter, statusFilter]);

  useEffect(() => {
    const id = setTimeout(search, 300);
    return () => clearTimeout(id);
  }, [search]);

  return (
    <>
      <Topbar title="Recherche & Archives" subtitle="Retrouvez n'importe quel PDF par numéro de transaction" />

      <main className="px-4 sm:px-6 lg:px-8 py-5 sm:py-8 max-w-[1600px] w-full" data-testid="recherche-page">
        <div className="bg-white border border-zinc-200 p-6 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-5">
              <div className="label-tiny text-zinc-500 mb-2">N° de transaction</div>
              <div className="relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  value={transactionNumber}
                  onChange={(e) => setTransactionNumber(e.target.value)}
                  placeholder="Ex: TRX-2026-0001"
                  className="h-11 pl-10 rounded-sm border-zinc-300 font-mono"
                  data-testid="global-transaction-search"
                />
              </div>
            </div>
            <div className="md:col-span-4">
              <div className="label-tiny text-zinc-500 mb-2">Recherche libre</div>
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Objet, description, fichier..."
                className="h-11 rounded-sm border-zinc-300"
                data-testid="global-text-search"
              />
            </div>
            <div className="md:col-span-2">
              <div className="label-tiny text-zinc-500 mb-2">Pôle</div>
              <Select value={poleFilter} onValueChange={setPoleFilter}>
                <SelectTrigger className="h-11 rounded-sm border-zinc-300" data-testid="filter-pole"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {poles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-1">
              <div className="label-tiny text-zinc-500 mb-2">Statut</div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-11 rounded-sm border-zinc-300" data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="unassigned">Non pris</SelectItem>
                  <SelectItem value="processing">En cours</SelectItem>
                  <SelectItem value="completed">Traité</SelectItem>
                  <SelectItem value="archived">Archivé</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="bg-white border border-zinc-200">
          <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
            <span className="label-tiny text-zinc-500">Résultats</span>
            <span className="font-mono text-xs text-zinc-600" data-testid="results-count">{items.length} document{items.length > 1 ? "s" : ""}</span>
          </div>
          {loading ? (
            <div className="px-6 py-12 text-center text-sm text-zinc-500">Chargement...</div>
          ) : !items.length ? (
            <div className="px-6 py-16 text-center text-sm text-zinc-500">Aucun résultat</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left px-4 py-3 label-tiny text-zinc-500">Date</th>
                  <th className="text-left px-4 py-3 label-tiny text-zinc-500">De → Vers</th>
                  <th className="text-left px-4 py-3 label-tiny text-zinc-500">Référence</th>
                  <th className="text-left px-4 py-3 label-tiny text-zinc-500">Statut</th>
                  <th className="text-left px-4 py-3 label-tiny text-zinc-500">N° Transaction</th>
                  <th className="text-right px-4 py-3 label-tiny text-zinc-500">Fichier</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`result-row-${r.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-600 whitespace-nowrap">{formatDate(r.created_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs">
                      <span className="font-medium">{poleLabel(r.sender_pole)}</span>
                      <span className="text-zinc-400 mx-1">→</span>
                      <span className="font-medium">{poleLabel(r.receiver_pole)}</span>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <div className="font-medium truncate">{r.object}</div>
                      <div className="text-xs text-zinc-500 truncate">{r.original_filename}</div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3 font-mono text-xs font-semibold">
                      {r.transaction_number || <span className="text-zinc-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.has_file ? (
                        <a
                          href={`${API}/requests/${r.id}/file`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 hover:bg-zinc-100 text-xs"
                          data-testid={`result-download-${r.id}`}
                        >
                          <DownloadSimple size={14} weight="bold" /> PDF
                        </a>
                      ) : (
                        <span className="text-[10px] text-zinc-400 font-mono">Sans PDF</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </>
  );
}
